import { Collapse, Stack, TableHead, Typography, alpha } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import PaiActionIconText from 'features/ui/base-components/pai-action-icon-text';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';
import { FunnelFunctionsLibrary } from './prompt-management.types';

interface Props {
  libraries: FunnelFunctionsLibrary[];
  moveFunctionLoading: boolean;

  handleOpenAddLibraryDialog(): void;

  handleOpenDeleteLibraryDialog(library: FunnelFunctionsLibrary): () => void;

  handleOpenEditLibraryDialog(library: FunnelFunctionsLibrary): () => void;

  handleOpenAddFunctionToLibraryDialog(library: FunnelFunctionsLibrary): () => void;

  handleOpenDeleteFunctionDialog(library: FunnelFunctionsLibrary, functionIndex: number): () => void;

  handleOpenEditFunctionDialog(library: FunnelFunctionsLibrary, functionIndex: number): () => void;

  handleMoveFunction(library: FunnelFunctionsLibrary, functionIndex: number, direction: -1 | 1): () => void;
}

const Prompt: React.FC<{ text: string }> = ({ text }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const [showMore, setShowMore] = useState(false);

  const textLength = 150;

  return (
    <div
      className={cx({
        [classes.messageText]: showMore,
      })}
      style={{ display: 'inline' }}
    >
      <Collapse in={!showMore} timeout={0}>
        {_.truncate(text, { length: textLength })}
        {text.length > textLength ? (
          <PaiLink
            text={showMore ? t('less') : t('more')}
            variant={'primary'}
            underline={'always'}
            size={'md'}
            inline={!showMore}
            ml={!showMore ? '4px' : undefined}
            action={(event) => {
              event.preventDefault();
              event.stopPropagation();

              setShowMore((prev) => !prev);
            }}
          />
        ) : null}
      </Collapse>
      <Collapse in={showMore} timeout={0}>
        {text}
        {text.length > textLength ? (
          <PaiLink
            text={showMore ? t('less') : t('more')}
            variant={'primary'}
            underline={'always'}
            size={'md'}
            inline={!showMore}
            ml={!showMore ? '4px' : undefined}
            action={(event) => {
              event.preventDefault();
              event.stopPropagation();

              setShowMore((prev) => !prev);
            }}
          />
        ) : null}
      </Collapse>
    </div>
  );
};

const PromptLibraries: React.FC<Props> = ({
  libraries,
  moveFunctionLoading,
  handleOpenAddLibraryDialog,
  handleOpenDeleteLibraryDialog,
  handleOpenEditLibraryDialog,
  handleOpenAddFunctionToLibraryDialog,
  handleOpenDeleteFunctionDialog,
  handleOpenEditFunctionDialog,
  handleMoveFunction,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  return (
    <div className={classes.promptLibraries}>
      <Table size={'small'} stickyHeader={true}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHead}>
              <Stack direction={'row'} justifyContent={'space-between'}>
                {t('libraries')}
                <PaiActionIconText variant={'add'} action={handleOpenAddLibraryDialog} />
              </Stack>
            </TableCell>
            <TableCell className={classes.tableHead}>{t('functions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!_.isEmpty(libraries) ? (
            libraries.map((library, idx1) => (
              <TableRow key={idx1}>
                <TableCell
                  className={cx(classes.tableBody, classes.libraryColum)}
                  sx={{ width: '250px', maxWidth: '250px', border: idx1 === libraries.length - 1 ? 'none' : undefined }}
                >
                  <Stack gap={1}>
                    <Stack direction={'row'} gap={1}>
                      <Typography className={classes.library}>{library.name}</Typography>
                      <PaiActionIconText variant={'delete'} dense={true} action={handleOpenDeleteLibraryDialog(library)} />
                      <PaiActionIconText variant={'edit'} dense={true} action={handleOpenEditLibraryDialog(library)} />
                    </Stack>
                    <Stack gap={0.5}>
                      <Typography className={classes.date}>{formatDateTime(new Date(library.createDate).toISOString(), 'Pp')}</Typography>
                      <Typography className={classes.libraryData}>{library.type}</Typography>
                    </Stack>
                    <PaiActionIconText variant={'add'} text={t('addFunction')} dense={true} action={handleOpenAddFunctionToLibraryDialog(library)} />
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{ padding: 0, width: 'calc(100% - 250px)', border: idx1 === libraries.length - 1 ? 'none' : undefined, verticalAlign: 'top' }}
                >
                  <Table size="small">
                    <TableBody>
                      {library.functions?.map((func, idx2, functions) => (
                        <TableRow key={idx2} className={classes.functionRow}>
                          <TableCell
                            className={classes.tableBody}
                            sx={{ width: '250px', maxWidth: '250px', border: idx2 === functions.length - 1 ? 'none' : undefined }}
                          >
                            <Typography className={classes.library}>{func.functionName}</Typography>
                          </TableCell>
                          <TableCell
                            className={classes.tableBody}
                            sx={{ width: 'calc(100% - 450px)', border: idx2 === functions.length - 1 ? 'none' : undefined }}
                          >
                            <Prompt text={func.prompt} />
                          </TableCell>
                          <TableCell
                            className={classes.tableBody}
                            sx={{
                              width: '200px',
                              maxWidth: '200px',
                              border: idx2 === functions.length - 1 ? 'none' : undefined,
                            }}
                          >
                            <Stack direction={'row'} gap={4} justifyContent={'flex-end'}>
                              <Stack direction={'row'} gap={2}>
                                {idx2 > 0 ? (
                                  <PaiActionIconText
                                    variant={'upward'}
                                    action={handleMoveFunction(library, idx2, -1)}
                                    disabled={moveFunctionLoading}
                                  />
                                ) : null}
                                {idx2 < functions.length - 1 ? (
                                  <PaiActionIconText
                                    variant={'downward'}
                                    action={handleMoveFunction(library, idx2, 1)}
                                    disabled={moveFunctionLoading}
                                  />
                                ) : null}
                              </Stack>
                              <Stack direction={'row'} gap={2}>
                                <PaiActionIconText variant={'delete'} action={handleOpenDeleteFunctionDialog(library, idx2)} />
                                <PaiActionIconText variant={'edit'} action={handleOpenEditFunctionDialog(library, idx2)} />
                              </Stack>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell className={classes.tableBody} colSpan={4} sx={{ textAlign: 'center' }}>
                {t('promptLibrariesNoPromptsFound')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  promptLibraries: {
    flexBasis: '100%',
    overflow: 'auto',
  },
  tableHead: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
  },
  tableBody: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    verticalAlign: 'top',
  },
  libraryColum: {
    borderRight: '1px solid rgba(224, 224, 224, 1)',
  },
  date: {
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.greys.medium,
  },
  libraryData: {
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.greys.medium,
  },
  library: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  functionRow: {
    '&:hover': {
      background: alpha('#000', 0.05),
    },
  },
  messageText: {
    whiteSpace: 'pre-wrap',
  },
  icon: {
    fontSize: theme.typography.pxToRem(16),
  },
}));

export default PromptLibraries;
